<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading || isProcessing"></p-progressBar>

<app-page-shell>
  <app-page-hero
    eyebrow="Accounting"
    title="Import Journal Entries"
    subtitle="Upload spreadsheets, validate each entry and line item, correct issues in place, and import into the general ledger.">
    <div actions class="hero-actions">
      <p-button
        label="Download Template"
        icon="pi pi-download"
        severity="secondary"
        [outlined]="true"
        (onClick)="downloadTemplate('xlsx')">
      </p-button>
      <p-button
        label="Back to Journal Entries"
        icon="pi pi-arrow-left"
        severity="secondary"
        [outlined]="true"
        (onClick)="navigateToJournalEntries()">
      </p-button>
    </div>
  </app-page-hero>

  <!-- Wizard Navigation Stepper Header -->
  <div class="wizard-stepper-container">
    <div
      class="wizard-step-item"
      [class.active]="activeStep === 1"
      [class.completed]="activeStep > 1"
      (click)="goToStep(1)">
      <div class="step-badge">1</div>
      <div class="step-info">
        <span class="step-title">Upload File</span>
        <span class="step-desc">Select Excel / CSV & defaults</span>
      </div>
    </div>
    <div class="step-divider"></div>
    <div
      class="wizard-step-item"
      [class.active]="activeStep === 2"
      [class.completed]="activeStep > 2"
      (click)="goToStep(2)">
      <div class="step-badge">2</div>
      <div class="step-info">
        <span class="step-title">Validate & Correct</span>
        <span class="step-desc">In-place live validation</span>
      </div>
    </div>
    <div class="step-divider"></div>
    <div
      class="wizard-step-item"
      [class.active]="activeStep === 3"
      [class.completed]="importSuccess">
      <div class="step-badge">3</div>
      <div class="step-info">
        <span class="step-title">Review & Import</span>
        <span class="step-desc">Confirm & post entries</span>
      </div>
    </div>
  </div>

  <!-- STEP 1: Upload & Defaults -->
  <ng-container *ngIf="activeStep === 1">
    <app-page-data-panel
      title="Upload Spreadsheet"
      subtitle="Supported formats: Microsoft Excel (.xlsx, .xls) and Comma-Separated Values (.csv).">
      <div class="upload-section">
        <div class="file-dropzone">
          <i class="pi pi-file-excel dropzone-icon"></i>
          <h4>Choose a spreadsheet or drag it here</h4>
          <p class="dropzone-hint">
            Each row represents a journal entry line. Rows with the same <strong>Entry Reference</strong> are automatically grouped into a single multi-line journal entry.
          </p>
          <label class="file-picker-btn">
            <i class="pi pi-folder-open"></i>
            <span>Browse Files</span>
            <input type="file" accept=".xlsx,.xls,.csv" (change)="onFileSelected($event)">
          </label>
          <div *ngIf="selectedFile" class="selected-file-pill">
            <i class="pi pi-check-circle"></i>
            <strong>{{ selectedFile.name }}</strong>
            <span>({{ (selectedFile.size / 1024).toFixed(1) }} KB)</span>
          </div>
        </div>

        <div class="defaults-card">
          <h5>Import Settings & Fallbacks</h5>
          <p class="text-muted">Used when the uploaded spreadsheet leaves specific header values blank.</p>

          <div class="form-grid">
            <div class="form-field">
              <label>Default Journal</label>
              <p-select
                [options]="journalOptions"
                [(ngModel)]="defaultJournalId"
                optionLabel="label"
                optionValue="journal.journalId"
                placeholder="Select Default Journal"
                styleClass="w-full">
              </p-select>
            </div>

            <div class="form-field">
              <label>Default Date</label>
              <p-datepicker
                [(ngModel)]="defaultEntryDate"
                dateFormat="yy-mm-dd"
                [showIcon]="true"
                styleClass="w-full">
              </p-datepicker>
            </div>

            <div class="form-field">
              <label>Default Currency</label>
              <input
                pInputText
                [(ngModel)]="defaultCurrencyCode"
                placeholder="SAR"
                class="w-full" />
            </div>
          </div>

          <div class="template-download-strip">
            <span>Need sample format?</span>
            <button
              pButton
              type="button"
              label="Excel Template (.xlsx)"
              icon="pi pi-file-excel"
              class="p-button-text p-button-sm"
              (click)="downloadTemplate('xlsx')">
            </button>
            <button
              pButton
              type="button"
              label="CSV Template (.csv)"
              icon="pi pi-file"
              class="p-button-text p-button-sm"
              (click)="downloadTemplate('csv')">
            </button>
          </div>
        </div>
      </div>

      <div class="step-footer-actions">
        <p-button
          label="Parse & Validate Entries"
          icon="pi pi-arrow-right"
          iconPos="right"
          [disabled]="!selectedFile"
          [loading]="isLoading"
          (onClick)="parseFileAndProceed()">
        </p-button>
      </div>
    </app-page-data-panel>
  </ng-container>

  <!-- STEP 2: Validate & Correct In-Place -->
  <ng-container *ngIf="activeStep === 2">
    <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

    <app-page-data-panel
      title="Validate & In-Place Correction"
      subtitle="Correct accounts, descriptions, dates, or amounts directly. Validation rules update immediately."
      [resultText]="filteredEntries.length + ' of ' + entries.length + ' entries'">

      <div actions class="panel-toolbar-actions">
        <div class="filter-group">
          <button
            pButton
            type="button"
            label="All ({{ summaryMetrics.totalEntries }})"
            [class.p-button-outlined]="filterStatus !== 'ALL'"
            severity="secondary"
            class="p-button-sm"
            (click)="filterStatus = 'ALL'">
          </button>
          <button
            pButton
            type="button"
            label="Invalid ({{ summaryMetrics.invalidEntries }})"
            [class.p-button-outlined]="filterStatus !== 'INVALID'"
            severity="danger"
            class="p-button-sm"
            (click)="filterStatus = 'INVALID'">
          </button>
          <button
            pButton
            type="button"
            label="Valid ({{ summaryMetrics.validEntries }})"
            [class.p-button-outlined]="filterStatus !== 'VALID'"
            severity="success"
            class="p-button-sm"
            (click)="filterStatus = 'VALID'">
          </button>
        </div>

        <div class="search-box">
          <i class="pi pi-search search-icon"></i>
          <input
            pInputText
            type="text"
            [(ngModel)]="searchQuery"
            placeholder="Search reference, journal, account..."
            class="p-inputtext-sm search-input" />
        </div>

        <p-button
          label="Re-validate All"
          icon="pi pi-refresh"
          severity="secondary"
          [outlined]="true"
          class="p-button-sm"
          (onClick)="validateAllEntries()">
        </p-button>

        <p-button
          label="Add Entry"
          icon="pi pi-plus"
          severity="secondary"
          [outlined]="true"
          class="p-button-sm"
          (onClick)="addManualEntry()">
        </p-button>

        <p-button
          label="Proceed to Review"
          icon="pi pi-arrow-right"
          iconPos="right"
          [disabled]="entries.length === 0"
          (onClick)="proceedToReview()">
        </p-button>
      </div>

      <!-- Entries List with In-Place Editable Lines -->
      <div class="entries-cards-container">
        <div *ngIf="filteredEntries.length === 0" class="empty-state">
          <i class="pi pi-filter-slash"></i>
          <p>No journal entries match the current filter criteria.</p>
        </div>

        <div
          *ngFor="let entry of filteredEntries; let entryIdx = index"
          class="entry-card"
          [class.entry-invalid]="!entry.isValid"
          [class.entry-valid]="entry.isValid">

          <!-- Entry Header Bar -->
          <div class="entry-header">
            <div class="entry-header-left">
              <button
                pButton
                type="button"
                [icon]="entry.expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                class="p-button-text p-button-rounded p-button-sm"
                (click)="entry.expanded = !entry.expanded">
              </button>

              <p-tag
                [value]="entry.isValid ? 'VALID' : (entry.errors.length + ' ERROR(S)')"
                [severity]="entry.isValid ? 'success' : 'danger'">
              </p-tag>

              <div class="entry-header-field">
                <label>Reference</label>
                <input
                  pInputText
                  [(ngModel)]="entry.reference"
                  (ngModelChange)="onFieldChange(entry)"
                  placeholder="Reference"
                  class="p-inputtext-sm" />
              </div>

              <div class="entry-header-field">
                <label>Date</label>
                <p-datepicker
                  [(ngModel)]="entry.entryDate"
                  (ngModelChange)="onFieldChange(entry)"
                  dateFormat="yy-mm-dd"
                  [showIcon]="true"
                  styleClass="p-inputtext-sm">
                </p-datepicker>
              </div>

              <div class="entry-header-field journal-field">
                <label>Journal</label>
                <p-select
                  [options]="journalOptions"
                  [(ngModel)]="entry.journalCode"
                  (onChange)="onJournalChange(entry)"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select Journal"
                  styleClass="p-inputtext-sm">
                </p-select>
              </div>
            </div>

            <div class="entry-header-right">
              <div class="balance-summary-pill" [class.unbalanced]="!entry.isBalanced">
                <span class="debit-sum">Dr: {{ entry.totalDebit | number:'1.2-2' }}</span>
                <span class="credit-sum">Cr: {{ entry.totalCredit | number:'1.2-2' }}</span>
                <span *ngIf="!entry.isBalanced" class="diff-badge">
                  Diff: {{ entry.balanceDifference | number:'1.2-2' }}
                </span>
                <span *ngIf="entry.isBalanced" class="balanced-badge">
                  <i class="pi pi-check"></i> Balanced
                </span>
              </div>

              <button
                pButton
                type="button"
                icon="pi pi-plus"
                label="Add Line"
                class="p-button-sm p-button-outlined p-button-secondary"
                (click)="addLine(entry)">
              </button>

              <button
                pButton
                type="button"
                icon="pi pi-trash"
                class="p-button-sm p-button-danger p-button-text p-button-rounded"
                pTooltip="Delete Entry"
                (click)="removeEntry(entryIdx)">
              </button>
            </div>
          </div>

          <!-- Entry Error Banner if Invalid -->
          <div *ngIf="!entry.isValid && entry.errors.length > 0" class="entry-errors-banner">
            <i class="pi pi-exclamation-triangle"></i>
            <div class="errors-list">
              <span *ngFor="let err of entry.errors" class="error-item">• {{ err }}</span>
            </div>
          </div>

          <!-- Lines Table (Collapsible) -->
          <div *ngIf="entry.expanded" class="entry-lines-container">
            <table class="in-place-table">
              <thead>
                <tr>
                  <th style="width: 3rem">#</th>
                  <th style="width: 28%">Account</th>
                  <th style="width: 28%">Description / Memo</th>
                  <th style="width: 15%" class="text-right">Debit</th>
                  <th style="width: 15%" class="text-right">Credit</th>
                  <th style="width: 5rem" class="text-center">Status</th>
                  <th style="width: 6rem" class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let line of entry.items; let lineIdx = index" [class.line-error]="!line.isValid">
                  <td class="text-muted">{{ lineIdx + 1 }}</td>

                  <!-- Account Select -->
                  <td>
                    <p-select
                      [options]="accountOptions"
                      [(ngModel)]="line.accountCode"
                      (onChange)="onAccountChange(entry, line)"
                      [filter]="true"
                      filterBy="label"
                      placeholder="Select Account"
                      styleClass="w-full p-inputtext-sm">
                    </p-select>
                    <small *ngIf="!line.account" class="text-danger">Account required</small>
                  </td>

                  <!-- Description -->
                  <td>
                    <input
                      pInputText
                      [(ngModel)]="line.description"
                      (ngModelChange)="onFieldChange(entry)"
                      placeholder="Description"
                      class="w-full p-inputtext-sm" />
                  </td>

                  <!-- Debit -->
                  <td>
                    <p-inputNumber
                      [(ngModel)]="line.debit"
                      (ngModelChange)="onFieldChange(entry)"
                      [min]="0"
                      [minFractionDigits]="2"
                      [maxFractionDigits]="2"
                      inputStyleClass="text-right p-inputtext-sm w-full"
                      styleClass="w-full">
                    </p-inputNumber>
                  </td>

                  <!-- Credit -->
                  <td>
                    <p-inputNumber
                      [(ngModel)]="line.credit"
                      (ngModelChange)="onFieldChange(entry)"
                      [min]="0"
                      [minFractionDigits]="2"
                      [maxFractionDigits]="2"
                      inputStyleClass="text-right p-inputtext-sm w-full"
                      styleClass="w-full">
                    </p-inputNumber>
                  </td>

                  <!-- Line Validation Status -->
                  <td class="text-center">
                    <i
                      *ngIf="line.isValid"
                      class="pi pi-check-circle text-success"
                      pTooltip="Valid line item">
                    </i>
                    <i
                      *ngIf="!line.isValid"
                      class="pi pi-times-circle text-danger"
                      [pTooltip]="line.errors.join(' | ')">
                    </i>
                  </td>

                  <!-- Line Actions -->
                  <td class="text-right">
                    <button
                      pButton
                      type="button"
                      icon="pi pi-calculator"
                      class="p-button-text p-button-sm p-button-help p-button-rounded"
                      pTooltip="Auto-balance this line"
                      (click)="autoBalanceLine(entry, line)">
                    </button>
                    <button
                      pButton
                      type="button"
                      icon="pi pi-trash"
                      class="p-button-text p-button-sm p-button-danger p-button-rounded"
                      pTooltip="Remove line"
                      (click)="removeLine(entry, lineIdx)">
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="step-footer-actions">
        <p-button
          label="Back to Upload"
          icon="pi pi-arrow-left"
          severity="secondary"
          [outlined]="true"
          (onClick)="goToStep(1)">
        </p-button>

        <p-button
          label="Proceed to Review & Import"
          icon="pi pi-arrow-right"
          iconPos="right"
          (onClick)="proceedToReview()">
        </p-button>
      </div>
    </app-page-data-panel>
  </ng-container>

  <!-- STEP 3: Review & Batch Import -->
  <ng-container *ngIf="activeStep === 3">
    <app-page-data-panel
      title="Review & Confirm Import"
      subtitle="Review balanced journal entries before committing them to the database.">

      <div *ngIf="!importSuccess" class="review-pre-import">
        <div class="review-summary-card">
          <div class="summary-metric">
            <span class="metric-num text-primary">{{ summaryMetrics.validEntries }}</span>
            <span class="metric-lbl">Valid Entries Ready</span>
          </div>
          <div class="summary-metric">
            <span class="metric-num">{{ summaryMetrics.totalDebits | number:'1.2-2' }}</span>
            <span class="metric-lbl">Total Debits ({{ companyCurrency }})</span>
          </div>
          <div class="summary-metric">
            <span class="metric-num">{{ summaryMetrics.totalCredits | number:'1.2-2' }}</span>
            <span class="metric-lbl">Total Credits ({{ companyCurrency }})</span>
          </div>
        </div>

        <div *ngIf="summaryMetrics.invalidEntries > 0" class="warning-callout">
          <i class="pi pi-exclamation-circle"></i>
          <div>
            <strong>Notice: {{ summaryMetrics.invalidEntries }} entry group(s) are invalid.</strong>
            <p>Only valid and balanced journal entries will be imported. Return to Step 2 to correct invalid entries if you wish to import them.</p>
          </div>
          <p-button
            label="Fix Invalid Entries"
            icon="pi pi-pencil"
            severity="warn"
            [outlined]="true"
            class="p-button-sm"
            (onClick)="goToStep(2)">
          </p-button>
        </div>

        <div class="step-footer-actions">
          <p-button
            label="Back to Editing"
            icon="pi pi-arrow-left"
            severity="secondary"
            [outlined]="true"
            (onClick)="goToStep(2)">
          </p-button>

          <p-button
            label="Import {{ summaryMetrics.validEntries }} Journal Entries"
            icon="pi pi-check"
            [loading]="isProcessing"
            [disabled]="summaryMetrics.validEntries === 0"
            (onClick)="executeImport()">
          </p-button>
        </div>
      </div>

      <!-- Post-Import Success Screen -->
      <div *ngIf="importSuccess" class="post-import-success">
        <div class="success-banner">
          <i class="pi pi-check-circle success-icon"></i>
          <h3>Import Complete!</h3>
          <p>Successfully created <strong>{{ importedResults.length }}</strong> journal entries in CargoMate.</p>
        </div>

        <p-table
          [value]="importedResults"
          dataKey="entryId"
          styleClass="p-datatable-gridlines mt-4"
          [tableStyle]="{ 'min-width': '50rem' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Entry Number</th>
              <th>Reference</th>
              <th>Journal</th>
              <th>Date</th>
              <th class="text-right">Amount</th>
              <th>Status</th>
              <th class="text-right">Action</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-entry>
            <tr>
              <td><strong>{{ entry.entryNumber || ('JE-' + entry.entryId) }}</strong></td>
              <td>{{ entry.reference || '-' }}</td>
              <td>{{ entry.journal?.journalName || '-' }}</td>
              <td>{{ entry.entryDate | date:'mediumDate' }}</td>
              <td class="text-right font-semibold">{{ companyCurrency }} {{ entry.amount | number:'1.2-2' }}</td>
              <td><p-tag [value]="entry.state || 'POSTED'" severity="success"></p-tag></td>
              <td class="text-right">
                <button
                  pButton
                  type="button"
                  label="View Entry"
                  icon="pi pi-external-link"
                  class="p-button-text p-button-sm"
                  (click)="viewImportedEntry(entry)">
                </button>
              </td>
            </tr>
          </ng-template>
        </p-table>

        <div class="step-footer-actions mt-4">
          <p-button
            label="Import Another File"
            icon="pi pi-upload"
            severity="secondary"
            [outlined]="true"
            (onClick)="resetWizard()">
          </p-button>

          <p-button
            label="View All Journal Entries"
            icon="pi pi-list"
            (onClick)="navigateToJournalEntries()">
          </p-button>
        </div>
      </div>
    </app-page-data-panel>
  </ng-container>
</app-page-shell>

